import React, { useState, useEffect } from 'react';
import { Building2, Plus, Search, Edit, Eye, Phone, Mail, Globe, MapPin, Loader2, X, Copy, MessageCircle, Share2, Key } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    description: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state: ''
  });
  const token = localStorage.getItem('token');
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordCompany, setResetPasswordCompany] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);

  const industries = [
    'Information Technology',
    'Banking & Finance',
    'Manufacturing',
    'Healthcare',
    'Education',
    'Consulting',
    'E-Commerce',
    'Telecommunications',
    'Automobile',
    'FMCG',
    'Retail',
    'Other'
  ];

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/placement/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await axios.put(`${API_URL}/api/placement/companies/${editingCompany.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Company updated successfully');
      } else {
        const response = await axios.post(`${API_URL}/api/placement/companies`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Company created successfully');
        // Show credentials modal with company info
        if (response.data.login_credentials) {
          setCredentials({
            ...response.data.login_credentials,
            companyName: formData.name,
            contactPhone: formData.contact_phone
          });
          setShowCredentialsModal(true);
        }
      }
      setShowModal(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      industry: '',
      website: '',
      description: '',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      city: '',
      state: ''
    });
    setEditingCompany(null);
  };

  const openEditModal = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      industry: company.industry || '',
      website: company.website || '',
      description: company.description || '',
      contact_person: company.contact_person || '',
      contact_email: company.contact_email || '',
      contact_phone: company.contact_phone || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || ''
    });
    setShowModal(true);
  };

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // WhatsApp share function
  const shareViaWhatsApp = (email, password, companyName, contactPhone = null) => {
    const message = `🏢 *Company Portal Login Credentials*

*Company:* ${companyName}

*Login URL:* ${window.location.origin}/company-portal

*Email:* ${email}
*Password:* ${password}

⚠️ Please change your password after first login.

Thank you!`;
    
    const encodedMessage = encodeURIComponent(message);
    
    // If contact phone is provided, open WhatsApp with that number
    if (contactPhone) {
      // Remove any non-numeric characters and add country code if not present
      let phone = contactPhone.replace(/\D/g, '');
      if (!phone.startsWith('91') && phone.length === 10) {
        phone = '91' + phone;
      }
      window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
    } else {
      // Open WhatsApp without pre-filled number
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    }
    toast.success('Opening WhatsApp...');
  };

  // Handle share credentials for existing company
  const handleShareCredentials = async (company) => {
    try {
      toast.loading('Generating new credentials...');
      const response = await axios.post(
        `${API_URL}/api/placement/companies/${company.id}/regenerate-credentials`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.dismiss();
      
      if (response.data.login_credentials) {
        const { email, password } = response.data.login_credentials;
        shareViaWhatsApp(
          email,
          password,
          response.data.company_name || company.name,
          response.data.contact_phone || company.contact_phone
        );
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error regenerating credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate credentials');
    }
  };

  // Handle admin reset password
  const handleResetPassword = async (company) => {
    setResetPasswordCompany(company);
    setResetPasswordResult(null);
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    try {
      toast.loading('Resetting password...');
      const response = await axios.post(
        `${API_URL}/api/placement/companies/${resetPasswordCompany.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.dismiss();
      
      setResetPasswordResult({
        password: response.data.new_password,
        name: response.data.company_name || resetPasswordCompany.name,
        email: response.data.company_email || resetPasswordCompany.contact_email,
        phone: response.data.company_phone || resetPasswordCompany.contact_phone
      });
      toast.success('Password reset successfully');
    } catch (error) {
      toast.dismiss();
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
      setShowResetPasswordModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
          <p className="text-sm text-gray-500 mt-1">Manage recruiting companies</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Company
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCompanies.map(company => (
          <div key={company.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{company.name}</h3>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">{company.industry}</span>
                  </div>
                </div>
                <button
                  onClick={() => openEditModal(company)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Edit className="h-4 w-4 text-gray-500" />
                </button>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>{company.contact_phone}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{company.contact_email}</span>
                </div>
                {company.website && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Globe className="h-4 w-4" />
                    <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                      {company.website}
                    </a>
                  </div>
                )}
                {company.city && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{company.city}, {company.state}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                <span className="text-gray-500">Jobs Posted: <span className="font-semibold text-gray-900">{company.total_jobs_posted || 0}</span></span>
                <span className="text-gray-500">Hired: <span className="font-semibold text-green-600">{company.total_hired || 0}</span></span>
              </div>
              
              {/* Share Credentials Button */}
              <div className="mt-3 pt-3 border-t flex gap-2">
                <button
                  onClick={() => handleShareCredentials(company)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                  data-testid={`share-credentials-${company.id}`}
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </button>
                <button
                  onClick={() => handleResetPassword(company)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium"
                  data-testid={`reset-password-${company.id}`}
                >
                  <Key className="h-4 w-4" />
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCompanies.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg">
          <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No companies found</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">{editingCompany ? 'Edit Company' : 'Add New Company'}</h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry *</label>
                  <select
                    required
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Industry</option>
                    {industries.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person *</label>
                  <input
                    type="text"
                    required
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingCompany ? 'Update' : 'Create'} Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentialsModal && credentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-green-600">Company Created Successfully!</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-600">Share these login credentials with the company for portal access:</p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Email:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{credentials.email}</span>
                    <button onClick={() => copyToClipboard(credentials.email)} className="p-1 hover:bg-gray-200 rounded">
                      <Copy className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Password:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{credentials.password}</span>
                    <button onClick={() => copyToClipboard(credentials.password)} className="p-1 hover:bg-gray-200 rounded">
                      <Copy className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                ⚠️ Please save these credentials. The password cannot be retrieved later.
              </p>
              
              {/* WhatsApp Share Button */}
              <button
                onClick={() => shareViaWhatsApp(
                  credentials.email, 
                  credentials.password, 
                  credentials.companyName || 'Company',
                  credentials.contactPhone
                )}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                data-testid="whatsapp-share-btn"
              >
                <MessageCircle className="h-5 w-5" />
                Send via WhatsApp
              </button>
              
              <button
                onClick={() => setShowCredentialsModal(false)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center gap-2 text-amber-600">
                <Key className="h-5 w-5" /> Reset Company Password
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {!resetPasswordResult ? (
                <>
                  <p className="text-gray-600">
                    Are you sure you want to reset password for <strong>{resetPasswordCompany?.name}</strong>?
                  </p>
                  <p className="text-sm text-gray-500">
                    A new password will be generated automatically.
                  </p>
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowResetPasswordModal(false)}
                      className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmResetPassword}
                      className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                    >
                      Reset Password
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-green-600 font-medium">Password reset successfully!</p>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Company:</span>
                      <span className="font-medium">{resetPasswordResult.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Email:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{resetPasswordResult.email}</span>
                        <button onClick={() => copyToClipboard(resetPasswordResult.email)} className="p-1 hover:bg-gray-200 rounded">
                          <Copy className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">New Password:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-green-600">{resetPasswordResult.password}</span>
                        <button onClick={() => copyToClipboard(resetPasswordResult.password)} className="p-1 hover:bg-gray-200 rounded">
                          <Copy className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
                    ⚠️ Please share this new password with the company. It cannot be retrieved later.
                  </p>
                  
                  <button
                    onClick={() => shareViaWhatsApp(
                      resetPasswordResult.email, 
                      resetPasswordResult.password, 
                      resetPasswordResult.name,
                      resetPasswordResult.phone
                    )}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Send via WhatsApp
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setResetPasswordResult(null);
                    }}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
